import React from "react";
import { Loader2 } from "lucide-react";

/** Reusable admin building blocks. Dark editorial palette. */

export const AdminPageHeader = ({ eyebrow, title, sub, action }) => (
  <div className="flex items-end justify-between gap-4 flex-wrap" data-testid="admin-page-header">
    <div>
      <p className="text-[10px] uppercase font-bold tracking-[0.4em]" style={{ color: "#FF9933" }}>
        ★ {eyebrow} ★
      </p>
      <h1
        className="font-display uppercase leading-[0.9] tracking-tight mt-2"
        style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", color: "#FDFBF7" }}
        data-testid="admin-page-title"
      >
        {title}
      </h1>
      {sub && <p className="text-sm font-medium text-neutral-400 mt-2 max-w-2xl">{sub}</p>}
    </div>
    {action}
  </div>
);

export const Stat = ({ label, value, accent, ink, testId }) => (
  <div
    className="p-4 border-2"
    style={{ background: accent || "#141414", borderColor: accent || "#1f1f1f", color: ink || "#FDFBF7" }}
    data-testid={testId}
  >
    <p className="text-[10px] uppercase font-bold tracking-[0.3em] opacity-80">{label}</p>
    <p className="font-display text-3xl md:text-4xl mt-1 leading-none">{value ?? "0"}</p>
  </div>
);

export const Card = ({ title, action, children, testId }) => (
  <section
    className="border-2 p-5 md:p-6"
    style={{ background: "#141414", borderColor: "#1f1f1f" }}
    data-testid={testId}
  >
    {(title || action) && (
      <header className="flex items-center justify-between gap-3 mb-4">
        {title && (
          <p className="text-[10px] uppercase font-extrabold tracking-[0.35em]" style={{ color: "#FF9933" }}>
            {title}
          </p>
        )}
        {action}
      </header>
    )}
    {children}
  </section>
);

export const EmptyState = ({ title, sub }) => (
  <div className="p-10 text-center border-2 border-dashed border-[#1f1f1f]" data-testid="admin-empty">
    <p className="font-display uppercase text-xl tracking-tight" style={{ color: "#FDFBF7" }}>
      {title || "Nothing here yet."}
    </p>
    {sub && <p className="text-sm text-neutral-500 mt-2">{sub}</p>}
  </div>
);

export const Spinner = ({ label = "Loading…" }) => (
  <div className="flex items-center gap-2 text-neutral-400 text-xs uppercase font-bold tracking-[0.3em]" data-testid="admin-spinner">
    <Loader2 size={14} className="animate-spin" strokeWidth={3} />
    {label}
  </div>
);

export const ErrorBanner = ({ message }) => (
  <div
    className="p-3 border-2 text-xs uppercase font-bold tracking-[0.25em]"
    style={{ background: "#2a0a0a", borderColor: "#E3000F", color: "#FFB1B1" }}
    data-testid="admin-error"
  >
    {message}
  </div>
);

export const Input = ({ className = "", ...props }) => (
  <input
    {...props}
    className={`bg-[#0A0A0A] border-2 border-[#1f1f1f] focus:border-[#FF9933] outline-none px-3 py-2 text-sm text-[#FDFBF7] placeholder:text-neutral-600 w-full ${className}`}
  />
);

export const Select = ({ className = "", children, ...props }) => (
  <select
    {...props}
    className={`bg-[#0A0A0A] border-2 border-[#1f1f1f] focus:border-[#FF9933] outline-none px-3 py-2 text-sm text-[#FDFBF7] w-full ${className}`}
  >
    {children}
  </select>
);

export const Textarea = ({ className = "", ...props }) => (
  <textarea
    {...props}
    className={`bg-[#0A0A0A] border-2 border-[#1f1f1f] focus:border-[#FF9933] outline-none px-3 py-2 text-sm text-[#FDFBF7] placeholder:text-neutral-600 w-full ${className}`}
  />
);

export const Btn = ({ children, variant = "primary", className = "", ...props }) => {
  const styles =
    variant === "primary"
      ? { background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }
      : variant === "danger"
      ? { background: "#E3000F", color: "#FFFFFF", borderColor: "#E3000F" }
      : variant === "ghost"
      ? { background: "transparent", color: "#FDFBF7", borderColor: "#1f1f1f" }
      : { background: "#FDFBF7", color: "#0A0A0A", borderColor: "#FDFBF7" };
  return (
    <button
      {...props}
      style={{ ...styles, padding: "0.55rem 0.9rem", borderWidth: 2, fontSize: "0.7rem", letterSpacing: "0.2em" }}
      className={`uppercase font-extrabold tracking-[0.2em] inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity ${className}`}
    >
      {children}
    </button>
  );
};

export const Pagination = ({ skip, limit, total, onChange }) => {
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));
  const prev = () => onChange(Math.max(0, skip - limit));
  const next = () => onChange(skip + limit < total ? skip + limit : skip);
  return (
    <div className="flex items-center justify-between gap-3 pt-3 border-t-2 border-[#1f1f1f] mt-3">
      <p className="text-[10px] uppercase font-bold tracking-[0.3em] text-neutral-500">
        Page {page} / {pages} · {total} total
      </p>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={prev} disabled={skip === 0} data-testid="page-prev">
          ← Prev
        </Btn>
        <Btn variant="ghost" onClick={next} disabled={skip + limit >= total} data-testid="page-next">
          Next →
        </Btn>
      </div>
    </div>
  );
};

export const Tag = ({ children, color = "#FF9933", testId }) => (
  <span
    className="inline-block px-2 py-0.5 text-[10px] uppercase font-extrabold tracking-[0.25em] border-2"
    style={{ color, borderColor: color, background: "transparent" }}
    data-testid={testId}
  >
    {children}
  </span>
);
